import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { isOfflineModeActive } from '../config/offlineAuth';

interface OnboardingStatus {
  isOnboarded: boolean;
  isDemo: boolean;
  company: {
    id: string;
    name: string;
    description: string | null;
    website: string | null;
    logoUrl: string | null;
    employeeCount: number | null;
    industry: string | null;
    revenueRange: string | null;
    businessBranches: string | null;
    aiPainPoints: string | null;
    aiAdoptionStage: string | null;
    contactPersonName: string | null;
    contactEmail: string | null;
    phone: string | null;
    addressStreet: string | null;
    addressCity: string | null;
    addressPostalCode: string | null;
    addressCountry: string | null;
    isOnboarded: boolean;
    isDemo: boolean;
  } | null;
}

interface UseOnboardingReturn {
  isOnboarded: boolean;
  isDemo: boolean;
  loading: boolean;
  error: string | null;
  company: OnboardingStatus['company'];
  refetch: () => Promise<void>;
}

export function useOnboarding(): UseOnboardingReturn {
  const { user } = useAuth();
  const [status, setStatus] = useState<OnboardingStatus>({
    isOnboarded: false,
    isDemo: false,
    company: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOnboardingStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Skip onboarding check in offline mode
      if (isOfflineModeActive()) {
        setStatus({
          isOnboarded: true,
          isDemo: true,
          company: null,
        });
        setLoading(false);
        return;
      }

      // Force skip onboarding if email is demo@securesight.ai.com
      if (user?.email === 'demo@securesight.ai.com') {
        setStatus({
          isOnboarded: true,
          isDemo: true,
          company: null,
        });
        setLoading(false);
        return;
      }

      const response = await fetch('/api/company/onboarding/status', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session-based auth
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Not authenticated - clear status
          setStatus({
            isOnboarded: false,
            isDemo: false,
            company: null,
          });
          setError('Not authenticated');
          setLoading(false);
          return;
        }

        if (response.status === 403) {
          // Not authorized - might not have a company
          setStatus({
            isOnboarded: false,
            isDemo: false,
            company: null,
          });
          setError('Not authorized to access onboarding status');
          setLoading(false);
          return;
        }

        if (response.status === 404) {
          // Company not found
          setStatus({
            isOnboarded: false,
            isDemo: false,
            company: null,
          });
          setLoading(false);
          return;
        }

        throw new Error(`Failed to fetch onboarding status: ${response.statusText}`);
      }

      const data: OnboardingStatus = await response.json();
      setStatus(data);
    } catch (err) {
      console.error('Error fetching onboarding status:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch onboarding status');
      setStatus({
        isOnboarded: false,
        isDemo: false,
        company: null,
      });
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    // Only fetch if user is authenticated
    if (user) {
      fetchOnboardingStatus();
    } else {
      // No user - clear status
      setStatus({
        isOnboarded: false,
        isDemo: false,
        company: null,
      });
      setLoading(false);
    }
  }, [user, fetchOnboardingStatus]);

  return {
    isOnboarded: status.isOnboarded,
    isDemo: status.isDemo,
    loading,
    error,
    company: status.company,
    refetch: fetchOnboardingStatus,
  };
}
